import { supabase } from "@/lib/supabase";
import type {
  Notification,
  NotificationPreference,
  NotificationPreferencesMap,
  NotificationPreferenceUpdate,
  NotificationType,
} from "@/types/notifications";

/**
 * Fetches all notification preferences for the current user
 */
export async function getUserNotificationPreferences(): Promise<{
  data: NotificationPreference[] | null;
  error: Error | null;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { data: null, error: new Error("User not authenticated") };
    }

    const { data, error } = await supabase
      .from("notification_preferences")
      .select("*")
      .eq("user_id", user.id)
      .order("notification_type");

    if (error) throw error;

    return { data: data as NotificationPreference[], error: null };
  } catch (error) {
    console.error("Error fetching notification preferences:", error);
    return { data: null, error: error as Error };
  }
}

/**
 * Fetches notification preferences as a map for easier lookup
 */
export async function getUserNotificationPreferencesMap(): Promise<{
  data: NotificationPreferencesMap | null;
  error: Error | null;
}> {
  const { data, error } = await getUserNotificationPreferences();

  if (error || !data) {
    return { data: null, error };
  }

  const preferencesMap: NotificationPreferencesMap = {};
  data.forEach((pref) => {
    preferencesMap[pref.notification_type] = {
      email_enabled: pref.email_enabled,
      sms_enabled: pref.sms_enabled,
      in_app_enabled: pref.in_app_enabled,
      push_enabled: pref.push_enabled,
    };
  });

  return { data: preferencesMap, error: null };
}

/**
 * Updates notification preferences for specific types
 */
export async function updateNotificationPreferences(
  updates: NotificationPreferenceUpdate[],
): Promise<{ success: boolean; error: Error | null }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: new Error("User not authenticated") };
    }

    // ⚡ Bolt: Optimize by batching updates into a single query
    // This reduces N network requests to just 1 request.
    if (updates.length === 0) {
      return { success: true, error: null };
    }

    const rowsToUpsert = updates.map((update) => {
      const { notification_type, ...channelPreferences } = update;
      return {
        user_id: user.id,
        notification_type,
        ...channelPreferences,
        updated_at: new Date().toISOString(),
      };
    });

    const { error } = await supabase
      .from("notification_preferences")
      .upsert(rowsToUpsert, {
        onConflict: "user_id,notification_type",
      });

    if (error) {
      throw error;
    }

    return { success: true, error: null };
  } catch (error) {
    console.error("Error updating notification preferences:", error);
    return { success: false, error: error as Error };
  }
}

/**
 * Updates a single preference for a notification type
 */
export async function updateSinglePreference(
  notificationType: NotificationType,
  channelUpdates: Partial<
    Omit<
      NotificationPreference,
      "id" | "user_id" | "notification_type" | "created_at" | "updated_at"
    >
  >,
): Promise<{ success: boolean; error: Error | null }> {
  return updateNotificationPreferences([
    {
      notification_type: notificationType,
      ...channelUpdates,
    },
  ]);
}

/**
 * Bulk update all notification types with same channel settings
 */
export async function bulkUpdateAllPreferences(
  channelUpdates: {
    email_enabled?: boolean;
    sms_enabled?: boolean;
    in_app_enabled?: boolean;
    push_enabled?: boolean;
  },
): Promise<{ success: boolean; error: Error | null }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: new Error("User not authenticated") };
    }

    // Get all user preferences
    const { data: preferences, error: fetchError } =
      await getUserNotificationPreferences();

    if (fetchError || !preferences) {
      return {
        success: false,
        error: fetchError || new Error("Failed to fetch preferences"),
      };
    }

    // Update all preferences. "Enable All" leaves Promotions (MARKETING) as they are: opting
    // in to promotions is a choice made on that row. "Disable All" includes them.
    const enablesAnything = Object.values(channelUpdates).some((on) => on === true);
    const updates = preferences
      .filter((pref) => !(enablesAnything && pref.notification_type === "MARKETING"))
      .map((pref) => ({
        notification_type: pref.notification_type,
        ...channelUpdates,
      }));

    return updateNotificationPreferences(updates);
  } catch (error) {
    console.error("Error bulk updating preferences:", error);
    return { success: false, error: error as Error };
  }
}

/**
 * Initialize default preferences for current user (usually called on first login)
 */
export async function initializeUserPreferences(): Promise<{
  success: boolean;
  error: Error | null;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: new Error("User not authenticated") };
    }

    // Call database function to initialize preferences
    const { error } = await supabase.rpc("init_user_notification_preferences", {
      p_user_id: user.id,
    });

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error("Error initializing preferences:", error);
    return { success: false, error: error as Error };
  }
}

// =============================================
// Notification Management Functions
// =============================================

/**
 * Fetches notifications for the current user
 */
export async function getUserNotifications(options?: {
  limit?: number;
  offset?: number;
  unreadOnly?: boolean;
  notificationType?: NotificationType;
}): Promise<{
  data: Notification[] | null;
  error: Error | null;
  count?: number;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { data: null, error: new Error("User not authenticated") };
    }

    let query = supabase
      .from("notifications")
      .select("*", { count: "exact" })
      .eq("recipient_id", user.id)
      .order("created_at", { ascending: false });

    // Apply filters
    if (options?.unreadOnly) {
      query = query.eq("is_read", false);
    }

    if (options?.notificationType) {
      query = query.eq("notification_type", options.notificationType);
    }

    // Apply pagination
    if (options?.limit) {
      query = query.limit(options.limit);
    }

    if (options?.offset) {
      query = query.range(
        options.offset,
        options.offset + (options.limit || 10) - 1,
      );
    }

    const { data, error, count } = await query;

    if (error) throw error;

    return { data: data as Notification[], error: null, count: count || 0 };
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return { data: null, error: error as Error };
  }
}

/**
 * Gets count of unread notifications
 */
export async function getUnreadNotificationCount(): Promise<{
  count: number;
  error: Error | null;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { count: 0, error: new Error("User not authenticated") };
    }

    const { count, error } = await supabase
      .from("notifications")
      .select("*", { count: "exact", head: true })
      .eq("recipient_id", user.id)
      .eq("is_read", false);

    if (error) throw error;

    return { count: count || 0, error: null };
  } catch (error) {
    console.error("Error fetching unread count:", error);
    return { count: 0, error: error as Error };
  }
}

/**
 * Marks a notification as read
 */
export async function markNotificationAsRead(
  notificationId: number,
): Promise<{ success: boolean; error: Error | null }> {
  try {
    const { error } = await supabase
      .from("notifications")
      .update({
        is_read: true,
        read_at: new Date().toISOString(),
      })
      .eq("id", notificationId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error("Error marking notification as read:", error);
    return { success: false, error: error as Error };
  }
}

/**
 * Marks all notifications as read for current user
 */
export async function markAllNotificationsAsRead(): Promise<{
  success: boolean;
  error: Error | null;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: new Error("User not authenticated") };
    }

    const { error } = await supabase
      .from("notifications")
      .update({
        is_read: true,
        read_at: new Date().toISOString(),
      })
      .eq("recipient_id", user.id)
      .eq("is_read", false);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    return { success: false, error: error as Error };
  }
}

/**
 * Deletes a notification
 */
export async function deleteNotification(
  notificationId: number,
): Promise<{ success: boolean; error: Error | null }> {
  try {
    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("id", notificationId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error("Error deleting notification:", error);
    return { success: false, error: error as Error };
  }
}

let notificationSubscriptionSeq = 0;

/**
 * Subscribes to real-time notification updates for current user.
 *
 * Each call gets its own channel topic: supabase-js returns the existing
 * channel for a repeated topic, and removeChannel() is async, so a fixed name
 * lets a remount (or a second subscriber) attach to a channel that is already
 * subscribed or being torn down and silently miss INSERTs.
 */
export function subscribeToNotifications(
  userId: string,
  callback: (notification: Notification) => void,
): { unsubscribe: () => void } {
  notificationSubscriptionSeq += 1;
  const channel = supabase
    .channel(`notifications-changes-${userId}-${notificationSubscriptionSeq}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `recipient_id=eq.${userId}`,
      },
      (payload) => {
        callback(payload.new as Notification);
      },
    )
    .subscribe();

  return {
    unsubscribe: () => {
      supabase.removeChannel(channel);
    },
  };
}
